'use client'

import { useEffect, useState } from 'react'
import { useTranslations } from 'next-intl'
import { useFieldArray, useForm } from 'react-hook-form'
import { CourseForm as CourseFormType } from '@/kmsApp/types/forms/course-form'
import AddressCommandPalette from '@/kmsApp/components/features/course/address-command-palette'
import SaveFavoriteDrawer from '@/kmsApp/components/features/course/save-favorite-drawer'
import { useUser } from '@/kmsApp/providers/user-provider'
import { useKmsService } from '@/kmsApp/providers/kms-service-provider'
import { useAddressPicker } from '@/kmsApp/hooks/use-address-picker'
import { useCourseEstimate } from '@/kmsApp/hooks/use-course-estimate'
import { useItinerarySteps } from '@/kmsApp/hooks/use-itinerary-steps'
import { usePaymentAvailability } from '@/kmsApp/hooks/use-payment-availability'
import { ServiceCourse } from '@/kmsApp/types/models/service-course'
import {
  createEmptyArret,
  getLabelAndIconFromOrdre,
  isEscale,
  isRoutableTrip,
} from '@/kmsApp/utils/course-stops'
import { ProgressionBar } from './ProgressionBar'
import { ItineraryRecap } from './ItineraryRecap'
import { TripInformations } from './TripInformations'
import { ClientInformation } from './ClientInformation'
import { StopInformation } from './StopInformation'
import { useCourseWizard } from './useCourseWizard'
import { useClientCourseSubmit } from './useClientCourseSubmit'
import { useTarifFixeSelection } from '@/kmsApp/hooks/use-tarif-fixe-selection'

type CourseFormProps = {
  idUtilisateurDefault?: number | null
  initialValues?: CourseFormType
}

const CourseForm = ({ idUtilisateurDefault = null, initialValues }: CourseFormProps) => {
  const t = useTranslations('web_course_form')
  const { me } = useUser()
  const { courseService } = useKmsService()
  const idUtilisateur = idUtilisateurDefault || me?.idContact

  const [servicesList, setServicesList] = useState<ServiceCourse[]>([])

  useEffect(() => {
    courseService.getAllServices().then(setServicesList)
  }, [courseService])

  const {
    register,
    watch,
    trigger,
    control,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<CourseFormType>({
    mode: 'onTouched',
    values: initialValues,
    defaultValues: {
      idEntreprise: me?.entrepriseActive?.idEntreprise ?? null,
      idContactDemandeur: null,
      demandeur: null,
      idContactPassager: null,
      passager: null,
      reservedFor: 'me',
      paymentType: -1,
      idService: 0,
      dateCourse: '',
      nombresPassagers: 1,
      nombresValises: 0,
      nombresSacs: 0,
      siegesEnfants: 0,
      commentaire: '',
      customData: {},
      avecTrajetRetour: false,
      arrets: [createEmptyArret(0), createEmptyArret(1)],
    },
  })

  const { fields, remove, insert, replace } = useFieldArray({ control, name: 'arrets' })

  // Le composant se monte parfois avant que `me` ne soit chargé : idEntreprise
  // vaudrait alors null pour toute la session. On le resynchronise donc, ce qui
  // suit aussi les changements d'entreprise active.
  useEffect(() => {
    setValue('idEntreprise', me?.entrepriseActive?.idEntreprise ?? null)
  }, [me, setValue])

  const hasClientStep = !me

  const { palette, favoriteDrawer, favorites, isLoadingFavorites } = useAddressPicker({
    idUtilisateur,
    watch,
    setValue,
  })

  // L'escale s'insère juste avant la destination, qui reste le dernier arrêt.
  const addEscale = () => {
    const lastIndex = fields.length - 1

    insert(lastIndex, createEmptyArret(lastIndex))
    setValue(`arrets.${lastIndex + 1}.ordre`, lastIndex + 1)
  }

  const removeEscale = (indexToRemove: number) => {
    remove(indexToRemove)
    // Les ordres doivent rester contigus après suppression.
    getValues('arrets').forEach((_, index) => {
      setValue(`arrets.${index}.ordre`, index)
    })
  }

  const itinerarySteps = useItinerarySteps(control)

  const stops = watch('arrets')
  const isTripDefined = isRoutableTrip(stops)

  // Les tarifs négociés appartiennent à une entreprise cliente : un visiteur
  // anonyme n'en a aucun, le backend n'est donc pas interrogé pour lui.
  const {
    tarifsFixes,
    isLoading: tarifsFixesLoading,
    tarifFixe,
    handleTarifFixeChange,
    infosTransport,
    setInfosTransport,
  } = useTarifFixeSelection({
    isAdmin: false,
    actif: !!me,
    setValue,
    replace,
  })

  const wizard = useCourseWizard({
    hasClientStep,
    fields,
    trigger,
    getValues,
    skipStopValidation: !!tarifFixe,
  })

  const {
    estimate,
    isLoading: estimateLoading,
    error: estimateError,
  } = useCourseEstimate({
    idService: watch('idService'),
    nombresPassagers: watch('nombresPassagers'),
    dateCourse: watch('dateCourse'),
    dateTrajetRetour: watch('dateTrajetRetour'),
    avecTrajetRetour: watch('avecTrajetRetour'),
    arrets: stops,
    // Sur un trajet préconfiguré, le prix est celui convenu : estimer
    // afficherait au client un montant qui ne sera pas facturé.
    actif: !tarifFixe,
  })

  const { paiementABordAutorise } = usePaymentAvailability({
    arrets: stops,
    paymentType: watch('paymentType'),
    setValue,
  })

  const { onSubmit, isSending } = useClientCourseSubmit(me, tarifFixe, infosTransport)

  const stepStyle = { width: `${wizard.stepWidthPercentage}%` }

  return (
    <div className="md:pl-14 md:pr-14 lg:pl-32 lg:pr-32 overflow-hidden">
      <ProgressionBar totalSteps={wizard.totalSteps} currentStep={wizard.currentStep} />

      <form
        id="course-form"
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-4 md:gap-8 w-full"
      >
        <div className="w-full overflow-hidden">
          {/* Les étapes défilent horizontalement : elles restent toutes montées,
              mais `inert` neutralise celles qui ne sont pas à l'écran. */}
          <div
            className="flex transition-transform duration-500 ease-in-out"
            style={{
              transform: `translateX(-${(wizard.currentStep - 1) * wizard.stepWidthPercentage}%)`,
              width: `${wizard.totalSteps * 100}%`,
            }}
          >
            {/* ─── Configuration du trajet (les arrêts) ─── */}
            <div
              style={stepStyle}
              className="shrink-0"
              inert={wizard.currentStep !== wizard.stopStepNumber}
            >
              <StopInformation
                fields={fields}
                control={control}
                register={register}
                errors={errors}
                currentStep={wizard.currentStep}
                openAddressSearch={palette.open}
                onNext={wizard.goToNextStep}
                isEscale={(ordre) => isEscale(ordre, fields.length)}
                addEscale={addEscale}
                removeEscale={removeEscale}
                getLabelAndIconFromOrdre={(ordre) =>
                  getLabelAndIconFromOrdre(ordre, fields.length, {
                    start: t('departure'),
                    end: t('destination'),
                    stopover: (n) => t('stopover', { n }),
                  })
                }
                openFavoriteDrawer={me ? favoriteDrawer.open : null}
                favorites={favorites}
                tarifsFixes={tarifsFixes}
                tarifsFixesLoading={tarifsFixesLoading}
                tarifFixeSelectionne={tarifFixe}
                onTarifFixeChange={handleTarifFixeChange}
                infosTransport={infosTransport}
                onInfosTransportChange={setInfosTransport}
              />
            </div>

            {/* ─── Détails du transport ─── */}
            <div
              style={stepStyle}
              className="shrink-0"
              inert={wizard.currentStep !== wizard.tripStepNumber}
            >
              <TripInformations
                control={control}
                register={register}
                setValue={setValue}
                errors={errors}
                currentStep={wizard.currentStep}
                prevStep={wizard.prevStep}
                onNext={wizard.goToNextStep}
                disabledSelect={!!tarifFixe}
                servicesList={servicesList}
              />
            </div>

            {/* ─── Client (visiteur non connecté uniquement) ─── */}
            {hasClientStep && (
              <div
                style={stepStyle}
                className="shrink-0"
                inert={wizard.currentStep !== wizard.clientStepNumber}
              >
                <ClientInformation
                  register={register}
                  errors={errors}
                  onNext={wizard.goToNextStep}
                  prevStep={wizard.prevStep}
                />
              </div>
            )}

            {/* ─── Récapitulatif ─── */}
            <div
              style={stepStyle}
              className="shrink-0"
              inert={wizard.currentStep !== wizard.recapStepNumber}
            >
              <ItineraryRecap
                control={control}
                register={register}
                itinerarySteps={itinerarySteps}
                errors={errors}
                prevStep={wizard.prevStep}
                canUseFacture={me?.entrepriseActive?.factureAutorisee ?? false}
                paiementABordAutorise={paiementABordAutorise}
                isSending={isSending}
                estimate={estimate}
                estimateLoading={estimateLoading}
                estimateError={estimateError}
                prixNegocie={tarifFixe?.prixFixe ?? null}
                libelleTarifFixe={tarifFixe?.libelle ?? null}
                serviceName={
                  servicesList.find((service) => service.id === watch('idService'))?.nom ?? null
                }
              />
            </div>
          </div>
        </div>
      </form>

      <AddressCommandPalette
        isOpen={palette.isOpen}
        onClose={palette.close}
        onSelect={palette.select}
        favorites={me ? favorites : []}
        isLoadingFavorites={me ? isLoadingFavorites : false}
      />

      {me && (
        <SaveFavoriteDrawer
          isOpen={favoriteDrawer.isOpen}
          onClose={favoriteDrawer.close}
          onSave={favoriteDrawer.save}
          isSaving={favoriteDrawer.isSaving}
          adresse={
            favoriteDrawer.index !== null
              ? watch(`arrets.${favoriteDrawer.index}.adresse`)
              : undefined
          }
        />
      )}
    </div>
  )
}

export default CourseForm
